// 홈 화면 제안 카드 2종 — PostSuggestCard(정보형·파란색), ProActiveSlotCard(액션형·클릭 가능)
import React from 'react';
import { AXBadge, AXButton, AXIconButton } from 'yukimushi-component-fe';
import { CalendarIcon, CardIcon, WarnIcon } from './Home.icons';
import type { ActionModalConfig, PostSuggestItem, ProActiveCardDef } from './Home.types';
import styles from './Home.startview.module.css';

/* ── 타입 1: AI가 추가로 감지한 업무 제안 카드 (정보형·파란색) ── */
export function PostSuggestCard({ item, idx, onClick, onActionModal }: {
  item: PostSuggestItem;
  idx: number;
  onClick?: () => void;
  onActionModal?: (config: ActionModalConfig) => void;
}) {
  const clickable = !!item.prompt || !!item.actionModal;
  return (
    <div
      className={`${styles.postSuggestCard} ${clickable ? styles.postSuggestCardClickable : ''}`}
      style={{ animationDelay: `${idx * 0.1}s` }}
    >
      <div className={styles.postSuggestCardTitle}>{item.title}</div>
      {item.metaType === 'date' && (
        <div className={styles.postSuggestMeta}>
          <CalendarIcon />
          <span>{item.metaLabel} <strong>{item.metaDate}</strong></span>
        </div>
      )}
      {item.metaType === 'warn' && (
        <div className={styles.postSuggestMetaWarn}>
          <WarnIcon /> 확인 필요
        </div>
      )}
      <div className={styles.postSuggestDesc}>
        {item.desc.split('\n').map((line, i, arr) => (
          <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
        ))}
      </div>
      <div className={styles.postSuggestFooter}>
        {item.actionStyle === 'solid' ? (
          <AXButton
            size="sm"
            background="#3B82F6"
            color="#fff"
            onClick={item.actionModal ? () => onActionModal?.(item.actionModal!) : onClick}
          >{item.actionLabel} →</AXButton>
        ) : (
          <AXButton
            size="sm"
            variant="outline"
            color="#3B82F6"
            borderColor="#3B82F6"
            hoverColor="#3B82F6"
            hoverBackground="#EEF4FF"
            onClick={item.actionModal ? () => onActionModal?.(item.actionModal!) : onClick}
          >{item.actionLabel} →</AXButton>
        )}
      </div>
    </div>
  );
}

/* ── 타입 2: ProActive 제안 카드 (home-ai-simple-* 설계서 구조) ── */
export function ProActiveSlotCard({
  card,
  idx,
  onClick,
  onSecondaryAction,
  onDismiss,
}: {
  card: ProActiveCardDef;
  idx: number;
  onClick: () => void;
  onSecondaryAction?: () => void;
  onDismiss?: () => void;
}) {
  return (
    <article
      className={styles.proActiveSimpleCard}
      style={{ animationDelay: `${idx * 0.06}s` }}
      onClick={onClick}
    >
      {/* 닫기 버튼 */}
      {onDismiss && (
        <div
          style={{ position: 'absolute', top: 10, right: 10 }}
          onClick={e => e.stopPropagation()}
        >
          <AXIconButton
            size={24}
            color="#CBD5E1"
            hoverColor="#64748B"
            hoverBackground="#F1F5F9"
            onClick={onDismiss}
            title="제안 닫기"
            icon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            }
          />
        </div>
      )}

      {/* 헤드: 아이콘 + 라벨 + 제목 */}
      <div className={styles.proActiveSimpleHead}>
        <div className={styles.proActiveSimpleIcon}>
          <CardIcon type={card.iconType} />
        </div>
        <div style={{ minWidth: 0 }}>
          {card.kicker && (
            <div style={{ marginBottom: 4 }}>
              <AXBadge
                label={card.kicker}
                shape="pill"
                background="#F8FAFC"
                textColor="#64748B"
                border
                borderColor="#E2E8F0"
              />
            </div>
          )}
          <div className={styles.proActiveSimpleTitle}>{card.title}</div>
        </div>
      </div>

      {/* 설명 */}
      <div className={styles.proActiveSimpleDesc}>{card.desc}</div>

      {/* 액션 버튼 */}
      <div className={styles.proActiveSimpleActions} onClick={e => e.stopPropagation()}>
        {card.actionStyle === 'solid' ? (
          <AXButton
            size="sm"
            background="#2563EB"
            color="#fff"
            onClick={onClick}
          >
            {card.actionLabel} →
          </AXButton>
        ) : (
          <AXButton
            size="sm"
            variant="outline"
            color="#2563EB"
            borderColor="#D7E3F4"
            hoverColor="#2563EB"
            hoverBackground="#F8FBFF"
            onClick={onClick}
          >
            {card.actionLabel} →
          </AXButton>
        )}
        {card.secondaryAction === 'mail-source' && onSecondaryAction && (
          <AXButton
            size="sm"
            variant="outline"
            color="#2563EB"
            borderColor="#D7E3F4"
            hoverColor="#2563EB"
            hoverBackground="#F8FBFF"
            onClick={onSecondaryAction}
          >
            원문 보기
          </AXButton>
        )}
      </div>
    </article>
  );
}
